import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton'; // Import TutorialButton
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/tipoequipamento.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const TipoEquipamentoManager = () => {
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_tp_equipamento: '',
    id_tp_projeto: '',
    descr_tp_equipamento: '',
    unidade: '',
    campos_habilitados: '',
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [tiposProjeto, setTiposProjeto] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const formatarCamposHabilitadosParaAPI = useCallback((camposString) => {
    if (typeof camposString !== 'string' || camposString.trim() === '') {
      return [];
    }
    return camposString.split(',').map(item => item.trim()).filter(item => item !== '');
  }, []);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchTiposProjeto = useCallback(async () => {
    try {
      const response = await api.get('/tipo-projeto');
      setTiposProjeto(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar tipos de projeto: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/tipo-equipamento');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchTiposProjeto();
  }, [verificarAPI, fetchTiposProjeto]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/tipo-equipamento');
      const dadosFormatados = response.data.data.map(item => ({
        ...item,
        campos_habilitados: Array.isArray(item.campos_habilitados) ? item.campos_habilitados.join(', ') : item.campos_habilitados
      }));
      setDados(dadosFormatados || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'tipo de equipamento', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/tipo-equipamento/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'tipo de equipamento', response.data);
      if (response && response.data) {
        const dadoAtualizado = {
          ...response.data,
          campos_habilitados: Array.isArray(response.data.campos_habilitados) ? response.data.campos_habilitados.join(', ') : response.data.campos_habilitados
        }
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([dadoAtualizado]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'tipo de equipamento');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'tipo de equipamento', { id });
    try {
      const response = await api.delete(`/tipo-equipamento/${id}`);
      logarSucesso('Excluir', 'tipo de equipamento', response.data);
      mostrarMensagem('success', 'Registro excluído com sucesso!');
      carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'tipo de equipamento');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const colunas = useMemo(() => [
    {
      title: 'Salvar',
      field: 'salvar',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        const dadosFormatados = {
          ...data,
          campos_habilitados: formatarCamposHabilitadosParaAPI(data.campos_habilitados)
        }
        atualizarRegistro(data.id_tp_equipamento, dadosFormatados);
      },
      headerSort: false
    },
    {
      title: 'Excluir',
      field: 'excluir',
      width: 80,
      hozAlign: 'center',
      formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
      cellClick: (e, cell) => {
        const data = cell.getRow().getData();
        if (window.confirm(`Deseja realmente excluir o registro "${data.id_tp_equipamento}"?`)) {
          excluirRegistro(data.id_tp_equipamento);
        }
      },
      headerSort: false
    },
    {
      title: 'ID',
      field: 'id_tp_equipamento',
      width: 150,
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar ID...',
      sorter: 'string'
    },
    {
      title: 'Tipo Projeto',
      field: 'id_tp_projeto',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar tipo projeto...',
      sorter: 'string',
      formatter: (cell) => {
        const val = cell.getValue();
        const tipo = tiposProjeto.find(t => t.id_tp_projeto === val);
        return tipo ? `${tipo.descr_tp_projeto} (${val})` : val;
      },
      editor: 'list',
      editorParams: {
        values: tiposProjeto.reduce((acc, t) => {
          acc[t.id_tp_projeto] = t.descr_tp_projeto;
          return acc;
        }, {}),
        autocomplete: true,
        clearable: true
      }
    },
    {
      title: 'Descrição',
      field: 'descr_tp_equipamento',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar descrição...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'Unidade',
      field: 'unidade',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar unidade...',
      sorter: 'string',
      editor: 'input'
    },
    {
      title: 'Campos Habilitados',
      field: 'campos_habilitados',
      headerFilter: 'input',
      headerFilterPlaceholder: 'Filtrar campos...',
      sorter: 'string',
      formatter: (cell) => {
        const value = cell.getValue();
        return Array.isArray(value) ? value.join(', ') : value;
      },
      editor: 'input'
    }
  ], [atualizarRegistro, excluirRegistro, tiposProjeto, formatarCamposHabilitadosParaAPI]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_tp_equipamento'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_tp_equipamento, id_tp_projeto, descr_tp_equipamento, unidade, campos_habilitados } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_tp_equipamento) {
      camposObrigatorios.push('ID');
      erros.push('id_tp_equipamento');
    }
    if (!id_tp_projeto) {
      camposObrigatorios.push('Tipo Projeto');
      erros.push('id_tp_projeto');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_tp_equipamento,
      id_tp_projeto,
      descr_tp_equipamento: descr_tp_equipamento === '' ? null : descr_tp_equipamento,
      unidade: unidade === '' ? null : unidade,
      campos_habilitados: formatarCamposHabilitadosParaAPI(campos_habilitados)
    };

    logarRequisicao('Adicionar', 'tipo de equipamento', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/tipo-equipamento', dataToSend);
      logarSucesso('Adicionar', 'tipo de equipamento', response.data);
      setFormData({ id_tp_equipamento: '', id_tp_projeto: '', descr_tp_equipamento: '', unidade: '', campos_habilitados: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
      // Recarrega os dados para pegar as informações completas do JOIN
      await carregarDados(false);
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'tipo de equipamento');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'tipo_equipamento.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => window.history.back()}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Tipos de Equipamento</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_tipo_equipamento</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/tipos de equipamento.mov" buttonText="Ver Tutorial Tipos de Equipamento" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_tp_equipamento">ID:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_tp_equipamento" name="id_tp_equipamento" value={formData.id_tp_equipamento} onChange={handleInputChange} placeholder="Ex: EQP" maxLength={50} disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('id_tp_equipamento') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="id_tp_projeto">Tipo projeto:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_tp_projeto"
              name="id_tp_projeto"
              value={formData.id_tp_projeto}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_tp_projeto') ? 'input-error' : ''}
            >
              <option value="">Selecione um tipo de projeto</option>
              {tiposProjeto.map(tipo => (
                <option key={tipo.id_tp_projeto} value={tipo.id_tp_projeto}>
                  {tipo.descr_tp_projeto} ({tipo.id_tp_projeto})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="descr_tp_equipamento">Descrição:</label>
            <input type="text" id="descr_tp_equipamento" name="descr_tp_equipamento" value={formData.descr_tp_equipamento} onChange={handleInputChange} placeholder="Ex: Cadeira" maxLength={100} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="unidade">Unidade:</label>
            <input type="text" id="unidade" name="unidade" value={formData.unidade} onChange={handleInputChange} placeholder="Ex: UN" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="campos_habilitados">Campos habilitados:</label>
            <input type="text" id="campos_habilitados" name="campos_habilitados" value={formData.campos_habilitados} onChange={handleInputChange} placeholder="Ex: cor, material" step="0.01" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>
            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span><strong>Campos obrigatórios:</strong> ID e Tipo Projeto</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default TipoEquipamentoManager;